import { Component, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BayclubsAlertService } from '@bayclubs/alert';
import { DropdownMultiselectItem } from '@bayclubs/drop-down';
import { TransactionService } from '@bayclubs/payments-processor-office-api';
import { PaymentProcessorOfficeLauncherService, PaymentsProcessorOfficeLauncherModel } from '@bayclubs/payments-processor-office-launcher';
import { AgePipe, ErrorTranslationService } from '@bayclubs/utils';
import { debounceTime, Subscription } from 'rxjs';
import { BottomMenuObserverBaseComponent } from '../../components/bottom-menu-observer-base/bottom-menu-observer-base.component';
import { campsenum } from '../../models/enums.generated';
import { SelectedMember } from '../../models/filters/selected-member.model';
import { camps } from '../../models/models.generated';
import { CampDefinitionApiService } from '../../services/api/camp-definition-api.service';
import { FilterApiService } from '../../services/api/filter-api.service';
import { SellCampApiService } from '../../services/api/sell-camp-api.service';
import { CampsEnumTranslationService } from '../../services/camps-enum-translation.service';
import { PaymentProcessorIntegrationService } from '../../services/payment-processor-integration.service';
import { PaymentsProcessorLaunchAuthService } from '../../services/payments-processor-launch-auth.service';

@Component({
    selector: 'app-sell-camp',
    host: { 'data-testid': 'sell-camp-page' },
    templateUrl: './sell-camp.component.html',
    styleUrls: [ './sell-camp.component.scss' ],
    providers: [ AgePipe ],
    standalone: false
})
export class SellCampComponent extends BottomMenuObserverBaseComponent implements OnInit, OnDestroy {

    bottomMenu = viewChild<ElementRef>('bottomMenu');
    bottomMenuHeight = signal(0);

    public filters: camps.OfficeFiltersContext = null;
    public clubDropdownItems: DropdownMultiselectItem[] = [];
    public campsDropDownItems: DropdownMultiselectItem[] = [];
    public campDetails: camps.SellCampDetailsModel = null;
    public selectedSession: camps.SellCampSessionModel = null;
    public summary: camps.PurchaseSummaryModel = null;
    public sessions: DropdownMultiselectItem[] = [];
    public variants: DropdownMultiselectItem[] = [];
    public options: DropdownMultiselectItem[] = [];
    public children: DropdownMultiselectItem[] = [];

    private allowedClubCodes = [ 'PCB', 'PCS' ];

    public waitingForPaymentProcessorResponse = false;

    public sellCampForm = new FormGroup({
        clubId: new FormControl(null, [ Validators.required ]),
        campId: new FormControl(null, [ Validators.required ]),
        sessionId: new FormControl(null, [ Validators.required ]),
        sessionVariantId: new FormControl(null, [ Validators.required ]),
        memberId: new FormControl(null, [ Validators.required ]),
        childId: new FormControl(null, [ Validators.required ]),
        optionIds: new FormControl([]),
        couponCode: new FormControl(null)
    });

    private subscription = new Subscription();
    private checkStatusSubscription = new Subscription();

    constructor(
        private translateService: CampsEnumTranslationService,
        private filtersService: FilterApiService,
        private campService: CampDefinitionApiService,
        private sellCampService: SellCampApiService,
        private transactionService: TransactionService,
        private launchAuth: PaymentsProcessorLaunchAuthService,
        private router: Router,
        private agePipe: AgePipe,
        private errorTranslate: ErrorTranslationService,
        private alert: BayclubsAlertService,
        private paymentProcessorIntegrationService: PaymentProcessorIntegrationService,
        private paymentsProcessorOfficeLauncherService: PaymentProcessorOfficeLauncherService) {
        super();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.checkStatusSubscription.unsubscribe();
    }


    ngOnInit(): void {
        this.loadFilters();
        this.subscribeToFormChanges();
    }

    private loadFilters() {
        this.filtersService.get(false).subscribe(data => {
            if (data.isSuccess) {
                this.filters = data.value;
                this.clubDropdownItems = data.value.clubs.filter(x => this.allowedClubCodes.includes(x.code)).map(x => {
                    return { id: x.id, value: x.shortName } as DropdownMultiselectItem
                });
            }
        });
    }


    private loadCamps() {
        const clubId = this.selectedClubId;

        if (!clubId) {
            return;
        }

        const selectedClub = this.filters?.clubs?.find(x => x.id == clubId);

        if (!selectedClub) {
            return;
        }

        this.clearFormAfterClubChanged();

        const searchRequest = {
            clubCodes: [ selectedClub.code ],
            statuses: [ campsenum.CampStatus.Active ],
            historyType: campsenum.HistorySearchType.Upcoming
        } as camps.CampDefinitionSearchRequest;

        this.campService.search(searchRequest).subscribe(x => {
            if (x.isSuccess) {
                this.campsDropDownItems = x.pageItems.map(x => {
                    return { id: x.id, value: x.name } as DropdownMultiselectItem
                });
            }
        })
    }

    onOptionsChanged(event) {
        console.log(event)
    }

    onMemberSelected(member: SelectedMember) {
        this.children = [];
        this.sellCampForm.controls.childId.setValue(null);
        if (!member) {
            this.sellCampForm.controls.memberId.setValue(null);
            this.sellCampForm.controls.childId.setValue(null);
            return;
        }

        this.sellCampForm.controls.memberId.setValue(member.mainMemberId);
        this.children = member.children.map(x => {
            return { id: x.childId, value: `${x.fullName} ${this.agePipe.transform(x.ageInMonths)}` } as DropdownMultiselectItem
        });
    }

    goToCheckout() {
        if (!this.sellCampForm.valid) {
            return;
        }

        const formValue = this.sellCampForm.value;

        if (!formValue.campId || !formValue.clubId || !formValue.childId ||
            !formValue.memberId || !formValue.sessionId || !formValue.sessionVariantId) {
            return;
        }

        const request = {
            campId: formValue.campId,
            childId: formValue.childId,
            memberId: formValue.memberId,
            sessionId: formValue.sessionId,
            sessionVariantId: formValue.sessionVariantId,
            clubId: formValue.clubId,
            optionIds: formValue.optionIds,
            couponCode: formValue.couponCode
        } as camps.GetSellCampSummaryRequest;

        this.sellCampService.createPurchase(request).subscribe(x => {
            if (x.isSuccess) {
                this.summary = x.value.summary;
                this.initPaymentProcessorTransaction(x.value);
            } else {
                this.errorTranslate.translateValidationNotifications(x.validationNotification);
                this.alert.showError(x.validationNotification.errors[0].message);
            }
        });
    }

    finalizePurchase() {
        if (!this.sellCampForm.valid) {
            return;
        }

        const formValue = this.sellCampForm.value;

        if (!formValue.campId || !formValue.clubId || !formValue.childId ||
            !formValue.memberId || !formValue.sessionId || !formValue.sessionVariantId) {
            return;
        }

        const request = {
            campId: formValue.campId,
            childId: formValue.childId,
            memberId: formValue.memberId,
            sessionId: formValue.sessionId,
            sessionVariantId: formValue.sessionVariantId,
            clubId: formValue.clubId,
            optionIds: formValue.optionIds,
            couponCode: formValue.couponCode
        } as camps.GetSellCampSummaryRequest;

        this.sellCampService.finalize(request).subscribe(x => {
            if (x.isSuccess) {
                this.summary = x.value.summary;
                this.router.navigate([ 'purchases/' + x.value.purchaseId + '/details' ]);
            } else {
                this.errorTranslate.translateValidationNotifications(x.validationNotification);
                this.alert.showError(x.validationNotification.errors[0].message);
            }
        });
    }

    private loadCampDetails() {
        const campId = this.selectedCampId;
        const clubId = this.selectedClubId;
        if (!campId || !clubId) {
            return;
        }

        this.clearFormAfterCampChanged();

        this.sellCampService.get(campId, clubId).subscribe(x => {
            if (x.isSuccess) {
                this.variants = [];
                this.sessions = [];
                this.campDetails = x.value;
                this.sessions = x.value.sessions.map(x => {
                    return { id: x.id, value: x.name } as DropdownMultiselectItem
                });
                this.sellCampForm.controls.sessionId.setValue(null);
            }
        })
    }

    private setVariants() {
        if (!this.selectedSessionId) {
            this.variants = [];
            this.options = [];
            this.selectedSession = null;
            return;
        }

        const session = this.campDetails.sessions.find(x => x.id == this.selectedSessionId);

        if (!session) {
            this.variants = [];
            this.options = [];
            this.selectedSession = null;
            return;
        }

        this.selectedSession = session;
        this.variants = session.variants.map(x => {
            return { id: x.id, value: `${x.name} - $ ${x.price}` } as DropdownMultiselectItem
        });
        this.options = session.options.map(x => {
            return { id: x.id, value: `${x.name} - $ ${x.price}` } as DropdownMultiselectItem
        });
        this.sellCampForm.controls.sessionVariantId.setValue(null);
        this.sellCampForm.controls.optionIds.setValue([]);
    }

    private subscribeToFormChanges() {

        this.subscription.add(
            this.sellCampForm.controls.clubId.valueChanges.subscribe(clubId => {
                this.loadCamps();
            }));

        this.subscription.add(
            this.sellCampForm.controls.campId.valueChanges.subscribe(campId => {
                this.loadCampDetails();
            }));

        this.subscription.add(
            this.sellCampForm.controls.sessionId.valueChanges.subscribe(sessionId => {
                this.setVariants();
            }));

        this.subscription.add(
            this.sellCampForm.valueChanges.pipe(debounceTime(500)).subscribe(value => {
                this.tryLoadSummary();
            }));
    }


    private tryLoadSummary() {
        const formValue = this.sellCampForm.value;

        if (!formValue.campId || !formValue.clubId || !formValue.childId ||
            !formValue.memberId || !formValue.sessionId || !formValue.sessionVariantId) {
            this.clearSummary();
            return;
        }

        const request = {
            campId: formValue.campId,
            childId: formValue.childId,
            memberId: formValue.memberId,
            sessionId: formValue.sessionId,
            sessionVariantId: formValue.sessionVariantId,
            clubId: formValue.clubId,
            optionIds: formValue.optionIds,
            couponCode: formValue.couponCode
        } as camps.GetSellCampSummaryRequest;

        this.sellCampService.getSummary(request).subscribe(x => {
            if (x.isSuccess) {
                this.summary = x.value
            } else {
                this.errorTranslate.translateValidationNotifications(x.validationNotification);
                this.alert.showError(x.validationNotification.errors[0].message);
            }
        });
    }

    private initPaymentProcessorTransaction(createPurchaseResponse: camps.CreateCampPurchaseResponse) {
        const request = this.paymentProcessorIntegrationService.mapToPaymentProcessorTransaction(createPurchaseResponse);

        this.transactionService.api10TransactionsPost(request).subscribe(response => {
            const ppRequest = {
                accessToken: this.launchAuth.accessToken,
                authMode: this.launchAuth.authMode,
                identityHintProvider: this.launchAuth.identityHintProvider,
                transactionId: response.transactionId,
                officeAppUrl: response.officeAppUrl,
                correlationId: response.correlationId,
                parentOrigin: window.location.origin,
                theme: 'dark',
                cancelOnModalClose: true
            } as PaymentsProcessorOfficeLauncherModel;

            this.waitingForPaymentProcessorResponse = true;
            this.paymentsProcessorOfficeLauncherService.open(ppRequest).subscribe(result => {
                if (result) {
                    this.checkStatusSubscription.add(
                        this.paymentProcessorIntegrationService.checkTransactionStatus(createPurchaseResponse.purchaseId)
                            .subscribe(waitingForPaymentProcessorResponse => {
                                if (waitingForPaymentProcessorResponse === false) {
                                    this.waitingForPaymentProcessorResponse = false;
                                }
                            })
                    );
                }
            })
        })
    }

    public get selectedClubId() {
        return this.sellCampForm.controls.clubId.value;
    }

    public get selectedCampId() {
        return this.sellCampForm.controls.campId.value;
    }

    public get selectedSessionId() {
        return this.sellCampForm.controls.sessionId.value;
    }

    public get canFinalizeWithoutPaying() {
        return this.summary && this.summary?.price == 0;
    }

    private clearSummary() {
        this.summary = null;
    }

    private clearFormAfterCampChanged() {
        this.sellCampForm.controls.sessionId.setValue(null, { emitEvent: false });
        this.sellCampForm.controls.sessionVariantId.setValue(null, { emitEvent: false });
        this.sellCampForm.controls.optionIds.setValue([], { emitEvent: false });
        this.sellCampForm.controls.couponCode.setValue(null, { emitEvent: false });
        this.sessions = [];
        this.variants = [];
        this.options = [];
        this.selectedSession = null;
    }

    private clearFormAfterClubChanged() {
        this.sellCampForm.controls.campId.setValue(null, { emitEvent: false });
        ;
        this.clearFormAfterCampChanged();
        this.campsDropDownItems = [];
    }
}
